import { Button, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import {
	Delete,
	Download,
	FolderInput,
	Pencil,
	Share2,
	Trash2,
} from "lucide-react";
import { type FC, useState } from "react";
import { toast } from "sonner";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { useDocumentDeleteMutation } from "web_myplann/Api/Document/useDocumentDeleteMutation";
import { useDocumentEditMutation } from "web_myplann/Api/Document/useDocumentEditMutation";
import { useIsDocumentTransferPending } from "web_myplann/Api/Document/useIsDocumentTransferPending";
import { useSaveDocumentMutation } from "web_myplann/Api/Document/useSaveDocumentMutation";
import { useShareDocumentMutation } from "web_myplann/Api/Document/useShareDocumentMutation";
import {
	type DocumentDTO,
	DocumentLocalTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DeleteDocumentDialog } from "web_myplann/Portfolio/Components/Document/DeleteDocumentDialog";
import { MoveDocumentDialog } from "web_myplann/Portfolio/Components/Document/MoveDocumentDialog";
import { downloadedDocumentsState } from "web_myplann/Portfolio/State/downloadedDocumentsAtom";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannInput } from "web_myplann/Shared/Components/MyPlannInput";
import { ActionsDrawerRedesign } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRedesign";
import type { DrawerAction } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRow";
import { device } from "web_myplann/Utils/device";

type Props = {
	item: DocumentDTO;
	open: boolean;
	onClose: () => void;
	activeTag?: string;
	isDownloaded?: boolean;
};

export const DocumentActionsDrawerRedesign: FC<Props> = ({
	item,
	open,
	onClose,
	activeTag,
	isDownloaded,
}) => {
	const { t } = useResource();
	const queryClient = useQueryClient();

	const setDownloadedDocuments = useSetAtom(downloadedDocumentsState);

	const { mutate: renameDocument } = useDocumentEditMutation();
	const { mutate: deleteDocument } = useDocumentDeleteMutation();
	const { mutateAsync: saveDocument, isPending: isSaving } =
		useSaveDocumentMutation();
	const { mutateAsync: shareDocument, isPending: isSharing } =
		useShareDocumentMutation();

	const isProcessing = useIsDocumentTransferPending();

	const [isRenameDrawerOpen, setIsRenameDrawerOpen] = useState(false);
	const [renameValue, setRenameValue] = useState(item.title);
	const [isMoveDrawerOpen, setIsMoveDrawerOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const isOverlayOpen =
		isRenameDrawerOpen || isMoveDrawerOpen || isDeleteDialogOpen;

	const isLocal = item.type === DocumentLocalTypeEnum.Local;

	const handleDownload = async () => {
		try {
			const downloaded = await saveDocument({
				id: item.id,
				fallbackTitle: item.title,
			});
			setDownloadedDocuments({ documents: downloaded });
		} catch {
			return;
		}
		onClose();
	};

	const handleRemoveDownload = async () => {
		const response = await device.removeAttachment(item.filename);
		if (response.payload.success) {
			setDownloadedDocuments({ documents: response.payload.downloaded });
		} else {
			toast.error(t(Resources.Common.somethingWentWrong));
		}
		onClose();
	};

	const handleShare = async () => {
		try {
			await shareDocument({ id: item.id, fallbackTitle: item.title });
		} catch {
			return;
		}
		onClose();
	};

	const handleRename = () => {
		renameDocument({
			requestContract: {
				title: renameValue,
			},
			id: item.id,
		});
		setIsRenameDrawerOpen(false);
		onClose();
		if (activeTag) {
			queryClient.invalidateQueries({
				queryKey: DocumentKeys.list(activeTag),
			});
		}
	};

	const R = Resources.Portfolio.List.DocumentActionsDrawer;

	const actions: DrawerAction[] = [
		isDownloaded
			? {
					key: "removeDownload",
					label: t(R.Actions.removeDownload),
					icon: Delete,
					onClick: handleRemoveDownload,
					disabled: isProcessing,
				}
			: {
					key: "download",
					label: t(R.Actions.download),
					icon: Download,
					onClick: handleDownload,
					isPending: isSaving,
					disabled: isProcessing,
				},
		{
			key: "share",
			label: t(R.Actions.share),
			icon: Share2,
			onClick: handleShare,
			isPending: isSharing,
			disabled: isProcessing,
		},
		...(isLocal
			? [
					{
						key: "rename",
						label: t(R.Actions.rename),
						icon: Pencil,
						onClick: () => setIsRenameDrawerOpen(true),
						disabled: isProcessing,
					} satisfies DrawerAction,
				]
			: []),
		{
			key: "move",
			label: t(R.Actions.move),
			icon: FolderInput,
			onClick: () => setIsMoveDrawerOpen(true),
			disabled: isProcessing,
		},
		...(isLocal
			? [
					{
						key: "delete",
						label: t(R.Actions.delete),
						icon: Trash2,
						onClick: () => setIsDeleteDialogOpen(true),
						isDestructive: true,
						disabled: isProcessing,
					} satisfies DrawerAction,
				]
			: []),
	];

	return (
		<>
			<ActionsDrawerRedesign
				open={open}
				onClose={onClose}
				title={item.title}
				concealed={isOverlayOpen}
				actions={actions}
			/>

			<MyPlannDrawer
				open={isRenameDrawerOpen}
				onClose={() => setIsRenameDrawerOpen(false)}
				title={t(R.Actions.rename)}
			>
				<Stack px={1.6} pb={1.6} gap={1.2}>
					<MyPlannInput
						label={t(R.Rename.fileRename)}
						value={renameValue}
						onChange={(e) => setRenameValue(e.target.value)}
					/>
					<Button onClick={handleRename}>{t(Resources.Common.save)}</Button>
				</Stack>
			</MyPlannDrawer>

			<MyPlannDrawer
				open={isMoveDrawerOpen}
				onClose={() => setIsMoveDrawerOpen(false)}
				title={t(R.fileDrawerTitle)}
			>
				<MoveDocumentDialog
					item={item}
					activeTag={activeTag}
					onClose={() => {
						setIsMoveDrawerOpen(false);
						onClose();
					}}
				/>
			</MyPlannDrawer>

			<DeleteDocumentDialog
				document={item}
				open={isDeleteDialogOpen}
				onClose={(result) => {
					setIsDeleteDialogOpen(false);
					if (result) {
						onClose();
						deleteDocument(item.id);
					}
				}}
			/>
		</>
	);
};
